import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'

import { persistor } from '../redux/store'
import { logoutUrl } from './hub'
import userManager from './oidc'
import { getSession, clearSession } from './tokenSource'

export const AuthContext = createContext(null)

const AuthProvider = ({ children }) => {
  const [state, setState] = useState({
    isLoading: true,
    isAuthenticated: false,
    user: null,
  })

  useEffect(() => {
    let isMounted = true

    const finish = (session) => {
      if (isMounted) {
        setState({
          isLoading: false,
          isAuthenticated: !!session,
          user: session ? session.user : null,
        })
      }
    }

    getSession().then(finish).catch(() => finish(null))

    return () => {
      isMounted = false
    }
  }, [])

  // Same name as the Auth0 SDK so the existing call sites stay untouched.
  const getAccessTokenSilently = useCallback(async () => {
    const session = await getSession()
    if (!session) {
      throw new Error('login_required')
    }
    return session.accessToken
  }, [])

  // Signup uses the same redirect: Cognito's sign-in page links to sign-up.
  // The /auth callback sends the user back to returnTo.
  const login = useCallback(({ returnTo } = {}) => (
    userManager.signinRedirect({ state: { returnTo } })
      .catch((error) => {
        console.error('Cognito sign-in redirect failed', error)
      })
  ), [])

  // Required pattern: clear our own session, then a full-page redirect to the Hub logout.
  // Only the Hub can clear its session cookie; it then ends the Cognito session and
  // sends the browser back to us. Never call Cognito /logout directly.
  const logout = useCallback(async () => {
    try {
      await clearSession()
      persistor.pause()
      await persistor.purge()
    } catch (error) {
      console.error('Local sign-out cleanup failed', error)
    }
    window.sessionStorage.clear()
    window.location.assign(logoutUrl(window.location.origin))
  }, [])

  const value = useMemo(() => ({
    ...state,
    getAccessTokenSilently,
    login,
    logout,
  }), [state, getAccessTokenSilently, login, logout])

  return (
    <AuthContext.Provider value={value}>
      { children }
    </AuthContext.Provider>
  )
}

export default AuthProvider
